import {Modal, Button, Spinner, Form} from 'react-bootstrap'
import Select from 'react-select'
import {parseId} from '../../../helpers'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useMutation, useQuery} from '@apollo/client'
import {IAllUserNode, IResAllListUsers} from '../../../types/users'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {REASSIGN_POLICIES_USER} from '../../../gql/mutations/policyMutation'

interface ModalReassignPoliciesProps {
  isOpen: boolean
  onClose: () => void
  updateListPolicies: () => void
}

interface IResAssignPolicies {
  assignPoliciesToUser: {
    success: boolean
    message: string
    errors?: {field: string; message: string}[]
  }
}

interface UserOption {
  value: string
  label: string
}

export const ModalReassignPolicies = ({
  isOpen,
  onClose,
  updateListPolicies,
}: ModalReassignPoliciesProps) => {
  const [disabledAssign, setDisabledAssign] = useState(true)
  const [userFrom, setUserFrom] = useState<UserOption | null>(null)
  const [userTo, setUserTo] = useState<UserOption | null>(null)
  const [userFromOptions, setUserFromOptions] = useState<UserOption[]>([])
  const [userToOptions, setUserToOptions] = useState<UserOption[]>([])

  const {
    data: dataMembersFrom,
    loading: loadingMember,
    error: errorMembers,
  } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'no-cache',
  })

  const [gqlCall, {data, loading, error}] = useMutation<IResAssignPolicies>(REASSIGN_POLICIES_USER)

  useEffect(() => {
    if (dataMembersFrom?.allUsersList) {
      // Filtrar solo usuarios con primaryGroup de Asesor, Supervisor o Administrador (excluir Agente)
      const filteredUsers = dataMembersFrom.allUsersList.filter((u) => {
        const primaryGroupName = u.primaryGroup?.name
        return primaryGroupName === 'Asesor' || primaryGroupName === 'Supervisor' || primaryGroupName === 'Administrador'
      })

      const options = filteredUsers.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
      }))
      setUserFromOptions(options)
    }
  }, [dataMembersFrom])

  useEffect(() => {
    if (dataMembersFrom?.allUsersList && userFrom) {
      // Filtrar solo usuarios con primaryGroup de Asesor, Supervisor o Administrador (excluir Agente)
      const filteredUsers = dataMembersFrom.allUsersList.filter((u) => {
        const primaryGroupName = u.primaryGroup?.name
        return (primaryGroupName === 'Asesor' || primaryGroupName === 'Supervisor' || primaryGroupName === 'Administrador') && u.id !== userFrom.value
      })

      const options = filteredUsers.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
      }))
      setUserToOptions(options)
    } else {
      setUserToOptions([])
    }
  }, [dataMembersFrom, userFrom])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
    if (errorMembers) toast.error(`Error: ${errorMembers.message}`)
  }, [error, errorMembers])

  useEffect(() => {
    setDisabledAssign(!(userFrom && userTo))
  }, [userTo, userFrom])

  useEffect(() => {
    if (!data) return
    if (data.assignPoliciesToUser?.success) {
      toast.success(data.assignPoliciesToUser?.message)
      updateListPolicies()
      modalClose()
    } else if (
      data.assignPoliciesToUser?.errors &&
      data.assignPoliciesToUser?.errors.length > 0
    ) {
      toast.error(`Error: ${data.assignPoliciesToUser?.errors[0]?.message}`)
    }
  }, [data])

  const handleReAssignPolicies = () => {
    if (!userFrom || !userTo) return
    gqlCall({
      variables: {
        fromUserId: parseId(userFrom.value) as number,
        toUserId: parseId(userTo.value) as number,
      },
    })
  }

  const modalClose = (): void => {
    setUserFrom(null)
    setUserTo(null)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={modalClose} size='lg' className='modal-reassign-policies'>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className='d-flex flex-column gap-0'>
            <span className='fw-bold'>Reassign policies by user</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex flex-column gap-4'>
          <div className='reassign-policies reassign-from'>
            <h2 className='fs-1'>
              From{' '}
              {loadingMember && (
                <span>
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </h2>
            {!loadingMember && dataMembersFrom && (
              <div>
                <Select
                  value={userFrom}
                  options={userFromOptions}
                  onChange={(selected) => setUserFrom(selected)}
                  placeholder='Please select a user'
                  isClearable
                  isSearchable
                />
              </div>
            )}
          </div>
          <div className='reassign-policies reassign-to'>
            <h2 className='fs-1'>
              To{' '}
              {loadingMember && (
                <span>
                  <Spinner animation='border' size='sm' />
                </span>
              )}
            </h2>
            {!loadingMember && userFrom && (
              <div>
                <Select
                  value={userTo}
                  options={userToOptions}
                  onChange={(selected) => setUserTo(selected)}
                  placeholder='Please select a user'
                  isClearable
                  isSearchable
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={(e) => {
            modalClose()
          }}
          variant='light'
        >
          Cancel
        </Button>
        <Button onClick={handleReAssignPolicies} variant='primary' disabled={disabledAssign}>
          {loading && <Spinner animation='border' className='me-2' size='sm' />}
          <i className='bi bi-person-up' />
          Reassign Policies
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
